import axios from 'axios';
import { LineType } from '@/vue/mixins/enums/LineTypeMixin.js';
import { FulfillmentType } from '@/vue/mixins/enums/FulfillmentType.js';
import {
    tentativePackagesFromTentatives,
    tentativeSectionsFromTentatives,
    tentativeServicesFromTentatives,
} from './tentativeHelpers.js';
import { PriceType } from '@/vue/mixins/enums/PriceType.js';
import { useDate } from '@/composables/useDate.js';

const { parseDate, add, daysDiff, parse } = useDate();

export function updatePackageBaseData(modifiedTentatives, modifiedTentativeConfig, fields) {
    modifiedTentativeConfig.los = fields.los_days;
    tentativeSectionsFromTentatives(modifiedTentatives).forEach((tentativeSection) => {
        if (LineType.isPackage(tentativeSection)) {
            if (FulfillmentType.isWholeDuration(tentativeSection)) {
                tentativeSection.start_time = fields.default_start;
                tentativeSection.end_time = fields.default_end;
                tentativeSection.start_date = 'day-1';
                tentativeSection.end_date = 'day-' + fields.los_days;
                tentativeSection.tentativeServices.forEach((tentativeService) => {
                    tentativeService.start_time = fields.default_start;
                    tentativeService.end_time = fields.default_end;
                    tentativeService.start_date = 'day-1';
                    tentativeService.end_date = 'day-' + fields.los_days;
                });
            }
            if (FulfillmentType.isAccommodation(tentativeSection) || FulfillmentType.isRoom(tentativeSection)) {
                tentativeSection.start_date = 'day-1';
                tentativeSection.end_date = 'day-' + fields.los_days;
                tentativeSection.tentativeServices.forEach((tentativeService) => {
                    tentativeService.start_date = 'day-1';
                    tentativeService.end_date = 'day-' + fields.los_days;
                });
            }
        }
    });
}

export function updateBaseData(state, modifiedParent) {
    let currentParent = {
        ...state.modifiedTentativeConfig.parent,
    };
    if (modifiedParent.term_id !== currentParent.term_id) {
        state.modifiedTentativeConfig.parent.term_id = modifiedParent.term_id;
    }

    if (
        state.modifiedTentativeConfig.parentType === 'Order' &&
        modifiedParent.rate_group_external_id !== currentParent.rate_group_external_id
    ) {
        const rate_group_external_id = modifiedParent.rate_group_external_id || modifiedParent.rate_group?.external_id;

        axios
            .post(
                '/business/vendors/' +
                    state.modifiedTentativeConfig.vendor.slug +
                    '/sales/helpers/tentative-sections/' +
                    rate_group_external_id,
                {
                    tentatives: state.modifiedTentatives,
                    modifiedParent: {
                        ...modifiedParent,
                        parent_type: state.modifiedTentativeConfig.parentType,
                    },
                }
            )
            .then((response) => {
                state.modifiedTentatives = response.data.tentatives;
                state.modifiedTentativeConfig.parent.rate_group_external_id = modifiedParent.rate_group_external_id;
                state.modifiedTentativeConfig.rateGroup = response.data.rateGroup;
            });
    }
    tentativePackagesFromTentatives(state.modifiedTentatives).forEach((tentativePackage) => {
        if (currentParent.start_date !== modifiedParent.start_date) {
            let diff = daysDiff(modifiedParent.start_date, currentParent.start_date);
            tentativePackage.start_date = parseDate(add(tentativePackage.start_date, diff, 'days'), 'yyyy-MM-dd');
            tentativePackage.changes.push('start_date');
        }
        if (currentParent.participants !== modifiedParent.participants) {
            if (tentativePackage.quantity > 0 && tentativePackage.quantity === currentParent.participants) {
                tentativePackage.quantity = modifiedParent.participants;
            }
        }
    });
    tentativeSectionsFromTentatives(state.modifiedTentatives).forEach((tentativeSection) => {
        if (!LineType.isAvailable(tentativeSection)) {
            if (
                FulfillmentType.isWholeDuration(tentativeSection) &&
                tentativeSection.start_date === currentParent.start_date &&
                tentativeSection.end_date === currentParent.end_date
            ) {
                if (
                    currentParent.start_time !== modifiedParent.start_time &&
                    tentativeSection.start_time === currentParent.start_time
                ) {
                    tentativeSection.start_time = modifiedParent.start_time;
                    tentativeSection.changes.push('start_time');
                }
                if (
                    currentParent.end_time !== modifiedParent.end_time &&
                    tentativeSection.end_time === currentParent.end_time
                ) {
                    tentativeSection.end_time = modifiedParent.end_time;
                    tentativeSection.changes.push('end_time');
                }
            }
            if (currentParent.start_date !== modifiedParent.start_date) {
                let diff = daysDiff(modifiedParent.start_date, currentParent.start_date);
                tentativeSection.start_date = parseDate(add(tentativeSection.start_date, diff, 'days'), 'yyyy-MM-dd');
                tentativeSection.end_date = parseDate(add(tentativeSection.end_date, diff, 'days'), 'yyyy-MM-dd');
                tentativeSection.changes.push('start_date');
                tentativeSection.changes.push('end_date');
            }
        }
    });
    tentativeServicesFromTentatives(state.modifiedTentatives).forEach((tentativeService) => {
        if (currentParent.participants !== modifiedParent.participants) {
            if (
                PriceType.isPerPersonBased(tentativeService) &&
                tentativeService.quantity > 0 &&
                tentativeService.quantity === currentParent.participants
            ) {
                tentativeService.quantity = modifiedParent.participants;
            }
        }
    });
    state.modifiedTentativeConfig.parent.start_date = modifiedParent.start_date;
    state.modifiedTentativeConfig.parent.start_time = modifiedParent.start_time;
    state.modifiedTentativeConfig.parent.end_date = modifiedParent.end_date;
    state.modifiedTentativeConfig.parent.end_time = modifiedParent.end_time;
    if (state.modifiedTentativeConfig.parentType === 'Proposal') {
        state.modifiedTentativeConfig.parent.expires_date = modifiedParent.expires_date;
    }
    state.modifiedTentativeConfig.parent.start_at = parse(
        modifiedParent.start_date + ' ' + modifiedParent.start_time
    ).toISOString();
    state.modifiedTentativeConfig.parent.end_at = parse(
        modifiedParent.end_date + ' ' + modifiedParent.end_time
    ).toISOString();
    state.modifiedTentativeConfig.parent.participants = modifiedParent.participants;
    state.modifiedTentativeConfig.parent.tags = modifiedParent.tags;
}
